import { useMutation } from "@tanstack/react-query";
import type { EvidencePack } from "@zworks/console-contracts";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, buildEvidencePack, verifyEvidencePack } from "@/lib/api";

/** Saves the signed pack as a JSON file, named for the run for filing. */
function downloadPack(pack: EvidencePack): void {
  const blob = new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `evidence-pack-${pack.runId.slice(0, 8)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function failureMessage(error: unknown): string {
  if (error instanceof ApiError) return error.problem?.detail ?? error.message;
  return "The request could not be completed.";
}

/**
 * The signed evidence pack for one run: artefact digests, test results, and approval
 * records, with a server signature the page verifies on export. The file downloads as
 * JSON; verification reads the signature, never a secret.
 */
export function EvidencePackPanel({ projectId, runId }: { projectId: string; runId: string }) {
  const [pack, setPack] = React.useState<EvidencePack | null>(null);
  const build = useMutation({
    mutationFn: () => buildEvidencePack(projectId, { runId }),
    onSuccess: (next) => setPack(next),
  });
  const verify = useMutation({
    mutationFn: (body: EvidencePack) => verifyEvidencePack(projectId, body),
  });

  const verification = verify.data;
  const signed = pack?.signed === true && pack.signature !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Evidence pack</CardTitle>
        <CardDescription>
          Artefact digests, test results, and approvals for this run, signed by the server.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={build.isPending}
            onClick={() => {
              verify.reset();
              build.mutate();
            }}
          >
            {build.isPending ? "Building pack" : "Download signed pack"}
          </Button>
          {pack ? (
            <Button
              variant="outline"
              disabled={verify.isPending}
              onClick={() => verify.mutate(pack)}
            >
              {verify.isPending ? "Verifying" : "Verify signature"}
            </Button>
          ) : null}
          {pack ? (
            <Button variant="outline" onClick={() => downloadPack(pack)}>
              Save file
            </Button>
          ) : null}
        </div>
        {pack ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={signed ? "success" : "secondary"}>
              {signed ? "Signed" : "Unsigned"}
            </Badge>
            <span className="text-muted-foreground tabular-nums">
              {pack.entries.length} {pack.entries.length === 1 ? "artefact" : "artefacts"} ·{" "}
              {pack.tests.length} {pack.tests.length === 1 ? "test" : "tests"} ·{" "}
              {pack.approvals.length} {pack.approvals.length === 1 ? "approval" : "approvals"}
            </span>
          </div>
        ) : null}
        {verification ? (
          <p role="status" className="text-sm text-muted-foreground">
            <Badge variant={verification.valid ? "success" : "danger"}>
              {verification.valid ? "Verified" : "Not verified"}
            </Badge>{" "}
            {verification.reason}
          </p>
        ) : null}
        {!signed && pack ? (
          <p className="text-xs text-muted-foreground">
            The server holds no pack key, so this pack ships unsigned. It still names every
            artefact, test, and approval.
          </p>
        ) : null}
        {build.isError ? (
          <p role="alert" className="text-sm text-danger">
            {failureMessage(build.error)}
          </p>
        ) : null}
        {verify.isError ? (
          <p role="alert" className="text-sm text-danger">
            {failureMessage(verify.error)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
